import { Injectable, computed, inject, signal } from '@angular/core';
import { CartItem, HiddenProduct, PricingError } from '@pedix-workspace/utils';
import { AnalyticsService, WebStorageService } from '@pedix-workspace/pedixapp-core-services';
import {
  RehydratedCartItems,
  consolidateCartItems,
  markCartItemProductsAsHidden,
  rehydrateCartItemsAgainstCatalog,
  syncCartItemPurchaseRestrictions,
  updateCartItemPrices,
} from '@pedix-workspace/orders';
import { CheckoutPreloaderService } from './features/order-checkout/checkout-preloader.service';
import { AppRequestContextService } from './app-request-context.service';
import { ApiV1_Catalog, ApiV1_Product } from '@pedix-workspace/api-v1';

const STORAGE_NAMESPACE = 'cart-state';

@Injectable({ providedIn: 'root' })
export class AppCartItemsService {
  private checkoutPreloaderService = inject(CheckoutPreloaderService);
  private appRequestContextService = inject(AppRequestContextService);
  private webStorage = inject(WebStorageService);
  private analyticsService = inject(AnalyticsService);

  private establishmentSlug: string;

  readonly #cartItems = signal<CartItem[]>([]);

  readonly cartItems = computed(() => this.#cartItems());
  readonly isCartEmpty = computed(() => this.cartItems().length === 0);

  get displaySku(): boolean {
    return this.appRequestContextService.establishment().catalogConfiguration?.displaySku || false;
  }

  initSession(establishmentSlug: string) {
    this.establishmentSlug = establishmentSlug;

    const cartItems = this.restoreSession(this.establishmentSlug);

    this.#cartItems.set(cartItems);
  }

  clearProducts() {
    this.storeSession(this.establishmentSlug, []);

    this.#cartItems.set([]);
  }

  setCartItems(cartItems: CartItem[]) {
    this.#cartItems.set(cartItems);

    this.storeSession(this.establishmentSlug, cartItems);
  }

  addCartItem(cartItem: CartItem) {
    this.#cartItems.update(cartItems =>
      consolidateCartItems([...cartItems, cartItem], this.displaySku),
    );

    this.storeSession(this.establishmentSlug, this.cartItems());

    this.analyticsService.addCartItem(cartItem);

    // Preloads checkout dependencies for better UX
    this.checkoutPreloaderService.preloadCheckout();
  }

  updateCartItem(cartItem: CartItem): CartItem[] {
    this.#cartItems.update(cartItems => {
      const cartItemIndex = cartItems.findIndex(
        currentCartItem => currentCartItem.id === cartItem.id,
      );
      if (cartItemIndex === -1) {
        return cartItems;
      }

      const oldCartItem = cartItems[cartItemIndex];

      // NOTE: this should not be here; probably is better using an observable with pairwise operator
      this.analyticsService.updateCartItem(oldCartItem, cartItem);

      return consolidateCartItems(
        Object.assign([], cartItems, { [cartItemIndex]: cartItem }),
        this.displaySku,
      );
    });

    this.storeSession(this.establishmentSlug, this.cartItems());

    return this.cartItems();
  }

  removeCartItem(cartItem: CartItem) {
    this.#cartItems.update(cartItems =>
      cartItems.filter(currentCartItem => currentCartItem.id !== cartItem.id),
    );

    this.storeSession(this.establishmentSlug, this.cartItems());

    this.analyticsService.removeCartItem(cartItem);
  }

  findCartItem(cartItemId: string): CartItem | undefined {
    return this.cartItems().find(cartItem => cartItem.id === cartItemId);
  }

  updateItemPrices(pricingErrors: PricingError[]) {
    this.#cartItems.update(cartItems => updateCartItemPrices(cartItems, pricingErrors));

    this.storeSession(this.establishmentSlug, this.cartItems());
  }

  syncPurchaseRestrictions(products: ApiV1_Product[]) {
    const syncedCartItems = syncCartItemPurchaseRestrictions(this.cartItems(), products);

    if (syncedCartItems !== this.cartItems()) {
      this.#cartItems.set(syncedCartItems);
      this.storeSession(this.establishmentSlug, syncedCartItems);
    }
  }

  markProductsHidden(hiddenProducts: HiddenProduct[]) {
    this.#cartItems.update(cartItems => markCartItemProductsAsHidden(cartItems, hiddenProducts));

    this.storeSession(this.establishmentSlug, this.cartItems());
  }

  getUpdatedCartItemsForSubsidiary(subsidiaryCatalog: ApiV1_Catalog) {
    return rehydrateCartItemsAgainstCatalog(
      this.cartItems(),
      subsidiaryCatalog.categories,
      subsidiaryCatalog.establishment.id,
    );
  }

  repeatCartItems(previousCartItems: CartItem[], catalog: ApiV1_Catalog): RehydratedCartItems {
    const result = rehydrateCartItemsAgainstCatalog(
      previousCartItems,
      catalog.categories,
      catalog.establishment.id,
    );

    this.setCartItems(
      consolidateCartItems([...this.cartItems(), ...result.updatedCartItems], this.displaySku),
    );

    return result;
  }

  private restoreSession(establishmentSlug: string): CartItem[] {
    const cartItems = this.webStorage.getItem<CartItem[]>(establishmentSlug, {
      storageType: 'session',
      prefix: 'pedixapp',
      namespace: STORAGE_NAMESPACE,
    });

    return cartItems || [];
  }

  private storeSession(establishmentSlug: string, cartItems: CartItem[]) {
    this.webStorage.setItem(establishmentSlug, cartItems, {
      storageType: 'session',
      prefix: 'pedixapp',
      namespace: STORAGE_NAMESPACE,
    });
  }
}
